import { type FC, useEffect } from 'react';
import { RouterProvider } from 'react-router';
import { router } from './router/router.provider';
import { AuthContext } from './contexts/auth/auth.context';
import { useAuth } from './hooks/useAuth';
import { AuthError, SessionStorageHelp } from '@Components/common/auth-required';
import Spinner from '@Components/common/spinner/Spinner';
import { AuthSession } from '@Shared/auth/auth-session';
import { isLocal } from '@Shared/env/env';

const MAX_DTRUM_RETRIES = 20;
const DTRUM_RETRY_INTERVAL_MS = 200;

declare global {
  interface Window {
    dtrum?: {
      identifyUser?: (tag: string) => void;
    };
  }
}

const AppContent: FC = () => {
  const authContextValue = useAuth();

  useEffect(() => {
    if (!authContextValue?.claims) return;

    const userTag = AuthSession.getInstance().getPreferredUsername();
    if (!userTag) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const tryIdentify = (retries: number) => {
      if (window.dtrum) {
        try {
          if (typeof window.dtrum.identifyUser === 'function') {
            window.dtrum.identifyUser(userTag);
          }
        } catch (e) {
          console.warn('Dynatrace: identifyUser failed', e);
        }
      } else if (retries < MAX_DTRUM_RETRIES) {
        timeoutId = setTimeout(() => tryIdentify(retries + 1), DTRUM_RETRY_INTERVAL_MS);
      } else if (!isLocal) {
        console.error('Dynatrace: dtrum did not load after maximum retries.');
      }
    };

    tryIdentify(0);
    return () => clearTimeout(timeoutId);
  }, [authContextValue?.claims]);

  if (authContextValue === undefined) {
    return <Spinner showSpinner={true} />;
  }

  if (authContextValue === null) {
    return isLocal ? <SessionStorageHelp /> : <AuthError />;
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      <RouterProvider router={router} />
    </AuthContext.Provider>
  );
};

export default AppContent;
